import React from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@lmn/design-system';
import {
  BaggageSection,
  CurrencyFormatterProvider,
  DictionaryProvider,
  type JourneyContractWay,
  journeyDetailsWayAdapter,
  TransportDetailsWay,
  useCurrencyFormatter,
  useDictionary,
  useDurationLabel,
  WAY_TRANSPORT_TYPES,
  waysFactory,
} from '@lmn/lmn-components';
/* Not re-exported from the package index, but reachable through its "./*" exports map and shipped
   with its own types. Deep-imported on purpose: rendering the library's own intro is what keeps the
   leg header identical to what TransportDetailsWay drew before MPH-958 moved it out. The '.js'
   is required, not optional: the exports map rewrites the subpath verbatim, so without an
   extension TypeScript has no file to hang the declarations off and answers TS2307. */
import { JourneyDetailsWayIntro } from '@lmn/lmn-components/components/Trip/Journey/JourneyDetails/Kallisto/JourneyDetailsWayIntro.js';
import { useTranslation } from '@/app/components/translations/Translation';
import { adaptWay } from '@/app/components/hotel-detail/flight-train/TransportJourneySummary';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { Co2FootPrint } from '@/app/components/hotel-detail/flight-train/Co2FootPrint';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import {
  getBoundArrivalCity,
  getBoundDepartureCity,
  getClassKey,
} from '@/app/components/hotel-detail/flight-train/TransportCommon';
import { parse } from 'date-fns';
import {
  adaptBaggage,
  isStopInDifferentStation,
  StationHopMessage,
} from '@/app/components/hotel-detail/flight-train/TransportJourneyDetails';
import { FLIGHTS_DATES_FORMAT } from '@/app/components/hotel-detail/common/dates';
import { starfishCssUrl } from '@/app/common/icons/starfish';
import { EvoLegWarningBanner } from './EvoLegWarningBanner';
import { evoLegWarnings } from './evoLegWarnings';

// #region styled

/**
 * The leg card, measured 1:1 against Figma 5193:56387: it carries the fill, the border and only
 * the *vertical* padding, so the header rule below can run the full width of the card while the
 * text either side of it stays inset — which is why the horizontal padding lives on the two
 * blocks inside instead. The 16px gap is the one between those blocks.
 */
const EvoWayCard = styled.div<{ $isTrain?: boolean }>`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.kallisto.space.space200};
  border-radius: ${(p) => p.theme.kallisto.border.radius.radius200};
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  background-color: ${(p) => p.theme.kallisto.color.background.static.backgroundLayer02};
  padding: ${(p) => `${p.theme.kallisto.space.space150} 0 ${p.theme.kallisto.space.space100}`};

  /* MPH-833: the return leg's plane must point the other way. The legs are NOT siblings inside a
     way — each sits in its own card — so a sibling selector on WayDetailsStyled never matched and
     both legs kept the outbound icon. Match on the cards, which are siblings.
     MPH-934: train.svg is not directional, so on T+H there is nothing to flip — and this rule has to
     drop rather than be overridden, its two-class selector outranking the wrapper-scoped base mask. */
  ${(p) =>
    !p.$isTrain &&
    css`
      & ~ & [class*='JourneyDetailsWayIntro__WayIntroTypeStyed']::before {
        mask-image: url('${starfishCssUrl('Transportation', 'icon-airplane-left')}');
      }
    `}
`;

/** Figma 5193:56388 — the header row and the rule under it, 12px apart. */
const EvoLegHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.kallisto.space.space150};
`;

/**
 * Figma 5193:56398 draws this rule edge to edge. The library's intro carries one as its own
 * border-bottom, but that one is inset by whatever padding the header text needs, so it is
 * suppressed below and replaced by this.
 */
const EvoLegHeaderRule = styled.div`
  height: 1px;
  background-color: ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
`;

/** Figma 5193:56399 — the banner and the itinerary, 12px apart, inset 16px. */
const EvoLegBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.kallisto.space.space150};
  padding: ${(p) => `0 ${p.theme.kallisto.space.space200} ${p.theme.kallisto.space.space100}`};
`;

const EvoWaysDivider = styled.div`
  margin: ${(p) => p.theme.kallisto.space.space200} 0;
`;

const EvoJourneyDetailsWrapper = styled.div<{ $isTrain?: boolean }>`
  /* MPH-958: the leg's stop list is composed under an intro and a banner of our own, so the
     library root stops being the frame of the card — EvoWayCard carries the fill and the border,
     and EvoLegBody the inset. The 8px is the one Figma 5193:56402 opens the itinerary with. */
  [class*='TransportDetailsWay__WayDetailsStyled'] {
    padding: ${(p) => p.theme.kallisto.space.space100} 0 0;
    background-color: transparent;
  }

  /* MPH-958: only the header text is inset; its rule is drawn by EvoLegHeaderRule so that it can
     span the whole card, as Figma 5193:56398 has it. The library's own border-bottom, and the
     spacing that goes with it, are handed over to EvoLegHeader's gap. */
  [class*='JourneyDetailsWayIntro__JourneyDetailsWayIntroStyled'] {
    display: flex !important;
    align-items: center !important;
    flex-wrap: wrap !important;
    gap: 0 !important;
    padding: ${(p) => `0 ${p.theme.kallisto.space.space200}`} !important;
    margin-bottom: 0 !important;
    border-bottom: none !important;
  }

  [class*='JourneyDetailsWayIntro__WayIntroTypeStyed'] {
    text-transform: none !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    line-height: 1.4 !important;
    color: ${(p) => p.theme.kallisto.color.text.static.textPrimary} !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
  }

  [class*='JourneyDetailsWayIntro__WayIntroTypeStyed']::before {
    content: '';
    display: inline-block;
    /* MPH-958: Figma 5193:56392 draws this at 20px. */
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    background-color: currentColor;
    mask-image: url('${(p) =>
      p.$isTrain
        ? starfishCssUrl('Transportation', 'icon-train')
        : starfishCssUrl('Transportation', 'icon-airplane-right')}');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
  }

  [class*='JourneyDetailsWayIntro__WayIntroTypeStyed']::after {
    content: ' ·';
    font-weight: 400;
    margin-right: 4px;
  }

  [class*='JourneyDetailsWayIntro__JourneyDetailsWayIntroStyled'] > *:nth-child(2) {
    font-size: 16px !important;
    font-weight: 400 !important;
    line-height: 1.4 !important;
    font-family: inherit !important;
  }

  [class*='JourneyDetailsWayIntro__JourneyDetailsWayIntroStyled'] > *:nth-child(n + 3) {
    display: none !important;
  }

  /* Hide the library's info messages by default (e.g. "4 nights in destination").
     MPH-833: matched exactly — a substring match also catches info-container-icon, so the icon
     of every message surfaced below was hidden in its own right and rendered 0x0. */
  [data-testid='info-container']:not(.keep-visible) {
    display: none;
  }

  /* MPH-398: surface the library's "Stop duration: Xh Ym" label between connecting segments */
  [class*='TransportStopBetweenSegments'] [data-testid='info-container'] {
    display: flex !important;
  }

  /* MPH-958: TransportDetailsSegment puts the warning colour on the Text that wraps the date and
     the child printing it hardcodes textSecondary, so a next-day date never actually turned
     orange. Inheriting lets the flag the segment already carries reach the glyphs. */
  [class*='TransportDetailsSegment__StyledDifferentDay'] {
    color: inherit;
  }
`;

const EvoTransportSummaryHeader = styled.div`
  padding-bottom: 16px;
`;

// MPH-833: Figma has this line in the primary text colour (#1E0023), not the grey it used to be —
// the secondary grey in that design is reserved for the "Terminal: 1" style detail lines.
const EvoTransportSummaryMeta = styled(Text)`
  color: ${(p) => p.theme.kallisto.color.text.static.textPrimary};
`;

const BaggageContainer = styled.div`
  margin-top: 16px;

  [class*='BaggageSection__Header'] {
    display: none;
  }

  [class*='Container'] > :nth-child(2) {
    border: 0;
    margin-top: 0;
    padding-top: 0;
  }
`;

const BaggageTitle = styled.div`
  margin-bottom: 12px;
`;

// #endregion

/** The library's way domain object, which it does not re-export by name. */
type Way = ReturnType<typeof waysFactory>[number];

type EvoTransportDetailsProps = {
  outbound: JourneyContractWay;
  inbound: JourneyContractWay;
  locale: string;
  seatSelectionMessage?: string[];
};

/** The calendar day a datetime falls on, in the timezone the itinerary is rendered in. */
/**
 * What TransportDetailsSegment's two different-day flags are set to. Only their truthiness is
 * read — the segment decides the date's colour from it and prints the formatted date itself, so
 * the value never reaches the screen. It is a constant rather than a dictionary lookup on purpose:
 * the copy key the library uses for it reads "+1 day", which is both wrong past one day and one
 * emptied CMS entry away from silently switching the highlight off.
 */
const DIFFERENT_DAY_FLAG = 'different-day';

const dayKey = (datetime: string): string => {
  const date = new Date(datetime);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

/**
 * MPH-958: the two legs of the itinerary, each with a single banner above its stop list carrying
 * every warning that leg has at once.
 *
 * The library offers two message slots, one above the stop list and one below it, and each takes a
 * string — so it can neither gather two warnings into one banner nor bullet them, which is what
 * Figma 5193-54698 draws. The leg is therefore composed here: the library's own intro (so the
 * header keeps its copy, its date format and its styling to the pixel), our banner, and
 * TransportDetailsWay for the stop list alone with both its message slots emptied.
 *
 * Passing `isIntroExpandable: false` to the adapter is what drops the collapse toggle Figma does
 * not draw, and hands the header divider back to the intro.
 */
function EvoTransportDetails({ outbound, inbound, locale, seatSelectionMessage }: EvoTransportDetailsProps) {
  const { isTrain } = useUrlSearchParams();
  const dictionary = useDictionary();
  const currencyFormatter = useCurrencyFormatter();
  const minutesToDurationLabel = useDurationLabel();
  const { t } = useTranslation();
  const [outboundWay, inboundWay] = waysFactory({ outbound, inbound });

  const renderWay = (way: Way, isInbound: boolean) => {
    const { introInfo, plusDays, priceInformation, segmentsDetails } = journeyDetailsWayAdapter.adapt(
      locale,
      way,
      dictionary,
      currencyFormatter,
      minutesToDurationLabel,
      seatSelectionMessage,
      false,
      false,
      false
    );

    const segments = way.getSegments();
    const messages = evoLegWarnings(
      {
        isInbound,
        /* Read off the leg's own segments rather than the search's isTrain, so a leg that mixes
           transports still names what its endpoints actually are. Only flights are airports; the
           copy has no third wording for coaches, so those read as stations. */
        placeKind: introInfo.allSegmentTypes.every((type) => type === WAY_TRANSPORT_TYPES.FLIGHT)
          ? 'airport'
          : 'station',
        departsFromDifferentLocation: segments[0].isDepartingOnDifferentLocation(),
        arrivesAtDifferentLocation: segments[segments.length - 1].isArrivingAtDifferentLocation(),
        plusDays,
      },
      t
    );

    /* Figma 5193-54698 paints in warning orange every date of the itinerary that is not the leg's
       departure day — all five "19 Aug" stops on the way home, not just the one that crosses
       midnight, which is all the library flags. The flags only drive the colour (the text printed
       is the formatted date), so recomputing them here is enough. */
    const departureDay = dayKey(segmentsDetails[0].departure.datetime);
    const segmentsWithDayFlags = segmentsDetails.map((segment) => ({
      ...segment,
      departure: {
        ...segment.departure,
        differentDayDepartureKey: dayKey(segment.departure.datetime) === departureDay ? undefined : DIFFERENT_DAY_FLAG,
      },
      arrival: {
        ...segment.arrival,
        differentDayArrivalKey: dayKey(segment.arrival.datetime) === departureDay ? undefined : DIFFERENT_DAY_FLAG,
      },
    }));

    return (
      <EvoWayCard $isTrain={isTrain}>
        <EvoLegHeader>
          <JourneyDetailsWayIntro {...introInfo} isInbound={isInbound} locale={locale} />
          <EvoLegHeaderRule />
        </EvoLegHeader>
        <EvoLegBody>
          <EvoLegWarningBanner messages={messages} />
          <TransportDetailsWay
            isInbound={isInbound}
            locale={locale}
            showBaggageInfo={false}
            priceInformation={priceInformation}
            segmentsDetails={segmentsWithDayFlags}
            /* Both of the library's own message slots are ours to draw now. */
            plusDays={0}
            differentLocationMessages={null}
            isDepartingOnDifferentDate={false}
          />
        </EvoLegBody>
      </EvoWayCard>
    );
  };

  return (
    <>
      {renderWay(outboundWay, false)}
      <EvoWaysDivider />
      {renderWay(inboundWay, true)}
    </>
  );
}

export type EvoTransportJourneyDetailsProps = {
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  co2Footprint: CO2Footprint;
  numberOfBaggagePiecesIncluded: number | null;
} & React.HTMLAttributes<HTMLElement>;

export function EvoTransportJourneyDetails({
  outbound,
  inbound,
  co2Footprint,
  numberOfBaggagePiecesIncluded,
}: EvoTransportJourneyDetailsProps) {
  const { localization } = usePageWrapperData();
  const { currency, locale } = localization;
  const { t, translationDictionary } = useTranslation();
  const { isTrain, children, adults, totalGuestsWithoutInfants } = useUrlSearchParams();

  const route = `${getBoundDepartureCity(outbound)} – ${getBoundArrivalCity(outbound)}`;
  // MPH-833: the flight's own dates, not the stay's. This body also serves the change-flight
  // modal, where the flight on screen is one the user has not selected — and an overnight
  // outbound departs the day before checkin, so the stay dates would contradict the itinerary
  // printed right below.
  const dateRangeLabel = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).formatRange(
    parse(outbound.departureDate, FLIGHTS_DATES_FORMAT, new Date()),
    parse(inbound.departureDate, FLIGHTS_DATES_FORMAT, new Date())
  );
  const classCode = outbound.segments[0]?.classAvailability;
  const flightClass = classCode ? t(getClassKey(classCode)) : '';
  const travellerLabel =
    totalGuestsWithoutInfants === 1
      ? t('vacation.oss.evo_flights.flight_details.travellers.single')
      : t('vacation.oss.evo_flights.flight_details.travellers.plural', { count: String(totalGuestsWithoutInfants) });
  const metaParts = [dateRangeLabel, travellerLabel, flightClass].filter(Boolean);
  const metaInfo = metaParts.join(' · ');

  return (
    <CurrencyFormatterProvider locale={locale} currency={currency} options={{ minimumFractionDigits: 0 }}>
      <DictionaryProvider labels={translationDictionary}>
        {/* MPH-833: parity with the legacy body — a connection that changes station must warn. */}
        {isTrain && (isStopInDifferentStation(outbound.segments) || isStopInDifferentStation(inbound.segments)) && (
          <StationHopMessage />
        )}
        <EvoJourneyDetailsWrapper $isTrain={isTrain}>
          <EvoTransportSummaryHeader>
            <Text variant="Title3">{route}</Text>
            {metaInfo && <EvoTransportSummaryMeta variant="Body">{metaInfo}</EvoTransportSummaryMeta>}
          </EvoTransportSummaryHeader>
          <EvoTransportDetails
            outbound={adaptWay(outbound, t)}
            inbound={adaptWay(inbound, t)}
            locale={locale}
            seatSelectionMessage={['VS']}
          />
        </EvoJourneyDetailsWrapper>
        {!isTrain && (
          <BaggageContainer>
            <BaggageTitle>
              <Text variant="Title4">{t('widget.trip_card.baggage_widget.included.title')}</Text>
            </BaggageTitle>
            <BaggageSection
              {...adaptBaggage({
                outbound,
                inbound,
                numberOfBaggagePiecesIncluded: numberOfBaggagePiecesIncluded ?? 0,
                numberOfLuggage: adults + children,
                t,
              })}
            />
          </BaggageContainer>
        )}
        {co2Footprint?.co2kg > 0 ? (
          <Co2FootPrint
            co2Footprint={co2Footprint}
            css={`
              margin-top: 16px;
            `}
          />
        ) : null}
      </DictionaryProvider>
    </CurrencyFormatterProvider>
  );
}
